import React, { useEffect, useState } from 'react';
import MaterialTable from 'material-table';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment/min/moment-with-locales';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import { downloadCsv, SECONDORY_COLOR } from '../common/sharedFunctions';
import { colors } from '../components/Theme/WebTheme';
import theme from 'styles/tableStyle';
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';

const getStatus = row =>
  row.status || (row.processed === true ? 'paid' : 'pending');

const statusPresentation = status => {
  if (status === 'paid') return { label: 'Paid', color: 'success' };
  if (status === 'approved') return { label: 'Approved', color: 'info' };
  if (status === 'rejected') return { label: 'Rejected', color: 'error' };
  if (status === 'failed') return { label: 'Failed', color: 'error' };
  if (status === 'initializing') return { label: 'Initializing', color: 'warning' };
  return { label: 'Pending', color: 'warning' };
};

const Withdraws = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const settings = useSelector(state => state.settingsdata.settings) || {};
  const withdrawdata = useSelector(state => state.withdrawdata);
  const { updateWithdrawalStatus } = api;
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [note, setNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const tx = (key, fallback) => t(key, { defaultValue: fallback });
  const formatAmount = value =>
    Number(value || 0).toLocaleString(
      settings.country === 'Vietnam' ? 'vi-VN' : 'en-IN',
      {
        minimumFractionDigits: Number(settings.decimal || 0),
        maximumFractionDigits: Number(settings.decimal || 0),
      }
    );
  const formatCurrency = value =>
    settings.swipe_symbol
      ? `${formatAmount(value)} ${settings.symbol || ''}`
      : `${settings.symbol || ''} ${formatAmount(value)}`;

  useEffect(() => {
    const source = Array.isArray(withdrawdata.withdraws)
      ? withdrawdata.withdraws
      : [];
    setData(source.map(item => ({ ...item, status: getStatus(item) })));
  }, [withdrawdata.withdraws]);

  const columns = [
    {
      title: tx('withdraw_status', 'Status'),
      field: 'status',
      defaultSort: 'asc',
      render: rowData => {
        const meta = statusPresentation(getStatus(rowData));
        return <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />;
      },
    },
    {
      title: tx('requestDate', 'Requested'),
      field: 'requestedAt',
      render: rowData => moment(rowData.requestedAt || rowData.date).format('DD MMM YYYY, hh:mm A'),
      exportTransformer: rowData =>
        new Date(rowData.requestedAt || rowData.date).toLocaleString(),
    },
    {
      title: tx('driver_name', 'Captain'),
      field: 'name',
      editable: 'never',
    },
    {
      title: tx('amount', 'Amount'),
      field: 'amount',
      editable: 'never',
      render: rowData => (
        <Typography fontWeight={700}>{formatCurrency(rowData.amount)}</Typography>
      ),
    },
    {
      title: tx('withdraw_method', 'Method'),
      field: 'payout.method',
      render: rowData => (
        <Chip
          size="small"
          label={(rowData.payout?.method || 'legacy').toUpperCase()}
          variant="filled"
        />
      ),
    },
    {
      title: tx('withdraw_account_holder', 'Account holder'),
      field: 'payout.accountHolderName',
      render: rowData =>
        rowData.payout?.accountHolderName ||
        rowData.payoutSummary?.accountHolderName ||
        '—',
    },
    {
      title: tx('withdraw_destination', 'Destination'),
      field: 'payoutDestination',
      render: rowData => {
        if (rowData.payout?.method === 'upi') return rowData.payout.upiId;
        if (rowData.payout?.method === 'bank') {
          return (
            <Stack spacing={0.25}>
              <Typography variant="body2">{rowData.payout.bankName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {rowData.payout.bankAccount}
              </Typography>
            </Stack>
          );
        }
        return rowData.bankAccount || 'Legacy request';
      },
    },
    {
      title: tx('bankCode', 'IFSC'),
      field: 'payout.bankCode',
      render: rowData => rowData.payout?.bankCode || rowData.bankCode || '—',
    },
    {
      title: tx('withdraw_payout_reference', 'Payout reference'),
      field: 'payoutReference',
      render: rowData => rowData.payoutReference || '—',
    },
    {
      title: tx('processDate', 'Processed'),
      field: 'processDate',
      render: rowData =>
        rowData.processDate ? moment(rowData.processDate).format('DD MMM YYYY, hh:mm A') : '—',
    },
  ];

  const openDialog = (type, row) => {
    setNote('');
    setDialog({ type, row });
  };

  const closeDialog = () => {
    if (processing) return;
    setDialog(null);
    setNote('');
  };

  const processAction = async () => {
    if (!dialog) return;
    if (dialog.type === 'paid' && note.trim().length < 3) {
      setSnackbar({
        severity: 'error',
        message: tx('withdraw_reference_required', 'Enter the bank or UPI payout reference.'),
      });
      return;
    }
    if (dialog.type === 'rejected' && note.trim().length < 3) {
      setSnackbar({
        severity: 'error',
        message: tx('withdraw_rejection_required', 'Enter a clear rejection reason.'),
      });
      return;
    }
    setProcessing(true);
    try {
      const metadata = dialog.type === 'paid'
        ? { payoutReference: note.trim() }
        : dialog.type === 'rejected'
          ? { rejectionReason: note.trim() }
          : {};
      await dispatch(
        updateWithdrawalStatus(dialog.row, dialog.type, metadata)
      );
      setSnackbar({
        severity: 'success',
        message: dialog.type === 'approved'
          ? tx('withdraw_approved_success', 'Withdrawal approved.')
          : dialog.type === 'paid'
            ? tx('withdraw_paid_success', 'Withdrawal marked as paid.')
            : tx('withdraw_rejected_success', 'Withdrawal rejected and refunded.'),
      });
      setDialog(null);
      setNote('');
    } catch (error) {
      setSnackbar({
        severity: 'error',
        message: error?.message || tx('withdraw_action_failed', 'Withdrawal action failed.'),
      });
    } finally {
      setProcessing(false);
    }
  };

  const exportCsv = (tableColumns, rows) => {
    const visibleColumns = tableColumns.filter(column => column.field);
    const headerRow = visibleColumns.map(column =>
      typeof column.title === 'string' ? column.title : column.field
    );
    const dataRows = rows.map(row =>
      visibleColumns.map(column => {
        if (column.field === 'requestedAt') {
          return new Date(row.requestedAt || row.date).toLocaleString();
        }
        return column.field.split('.').reduce(
          (value, key) => value?.[key],
          row
        ) ?? '';
      })
    );
    downloadCsv(
      [headerRow, ...dataRows].map(values => values.join(',')).join('\n'),
      'withdrawals.csv'
    );
  };

  if (withdrawdata.loading) return <CircularLoading />;

  const dialogTitle = dialog?.type === 'approved'
    ? tx('withdraw_approve_title', 'Approve withdrawal')
    : dialog?.type === 'paid'
      ? tx('withdraw_paid_title', 'Confirm external payout')
      : tx('withdraw_reject_title', 'Reject and refund');

  return (
    <ThemeProvider theme={theme}>
      <MaterialTable
        title={tx('Withdraw_title', 'Withdrawals')}
        columns={columns}
        data={data}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: '14px',
          boxShadow: `0 8px 28px ${SECONDORY_COLOR}`,
          padding: '20px',
        }}
        onRowClick={(event, row) => setSelectedRow(row.tableData.id)}
        options={{
          exportCsv,
          exportButton: {
            csv: settings.AllowCriticalEditsAdmin,
            pdf: false,
          },
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          actionsColumnIndex: -1,
          rowStyle: row => ({
            backgroundColor:
              selectedRow === row.tableData.id ? colors.ROW_SELECTED : colors.WHITE,
          }),
          ...TableStyle(),
        }}
        localization={localization(t)}
        actions={[
          row => ({
            icon: 'verified',
            tooltip: tx('withdraw_approve', 'Approve'),
            hidden: getStatus(row) !== 'pending',
            onClick: () => openDialog('approved', row),
          }),
          row => ({
            icon: 'payments',
            tooltip: tx('withdraw_mark_paid', 'Mark paid'),
            hidden: getStatus(row) !== 'approved',
            onClick: () => openDialog('paid', row),
          }),
          row => ({
            icon: 'cancel',
            tooltip: tx('withdraw_reject', 'Reject and refund'),
            hidden: !['pending', 'approved'].includes(getStatus(row)),
            onClick: () => openDialog('rejected', row),
          }),
        ]}
      />

      <Dialog open={!!dialog} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity={dialog?.type === 'rejected' ? 'warning' : 'info'}>
              {dialog?.type === 'approved'
                ? tx(
                  'withdraw_approve_message',
                  'Verify the destination and amount before approval. No money is transferred by this action.'
                )
                : dialog?.type === 'paid'
                  ? tx(
                    'withdraw_paid_message',
                    'Use this only after the external bank or UPI transfer succeeds.'
                  )
                  : tx(
                    'withdraw_reject_message',
                    'Rejecting automatically returns the reserved amount to the captain wallet.'
                  )}
            </Alert>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">{tx('driver_name', 'Captain')}</Typography>
              <Typography fontWeight={700}>{dialog?.row?.name}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">{tx('amount', 'Amount')}</Typography>
              <Typography fontWeight={700}>{formatCurrency(dialog?.row?.amount)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">{tx('withdraw_destination', 'Destination')}</Typography>
              <Typography fontWeight={700}>
                {dialog?.row?.payout?.method === 'upi'
                  ? dialog.row.payout.upiId
                  : dialog?.row?.payoutSummary?.label || dialog?.row?.bankAccount || '—'}
              </Typography>
            </Stack>
            {dialog?.type === 'paid' ? (
              <TextField
                autoFocus
                fullWidth
                label={tx('withdraw_payout_reference', 'Bank / UPI reference')}
                value={note}
                onChange={event => setNote(event.target.value)}
                helperText={tx(
                  'withdraw_reference_help',
                  'Enter the UTR, RRN, or bank transaction reference.'
                )}
              />
            ) : null}
            {dialog?.type === 'rejected' ? (
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                label={tx('withdraw_rejection_reason', 'Rejection reason')}
                value={note}
                onChange={event => setNote(event.target.value)}
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeDialog} disabled={processing}>
            {tx('cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color={dialog?.type === 'rejected' ? 'error' : 'primary'}
            onClick={processAction}
            disabled={processing}
          >
            {processing
              ? tx('processing', 'Processing…')
              : dialog?.type === 'approved'
                ? tx('withdraw_approve', 'Approve')
                : dialog?.type === 'paid'
                  ? tx('withdraw_mark_paid', 'Mark paid')
                  : tx('withdraw_reject_refund', 'Reject and refund')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(null)}
          severity={snackbar?.severity || 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default Withdraws;
